import React, { useState } from 'react';
import FileUpload from './FileUpload';
import { createTicket, uploadAttachment } from '../utils/supportUtils';

const CreateTicket = ({ user, onTicketCreated, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium'
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (files) => {
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleFileRemove = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validate form
      if (!formData.title.trim() || !formData.description.trim()) {
        setError('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }

      // Create the ticket
      const ticketData = {
        user_id: user.id,
        user_email: user.email,
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority
      };

      const { data: ticket, error: ticketError } = await createTicket(ticketData);

      if (ticketError) {
        setError('Failed to create ticket. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Upload attachments if any
      if (selectedFiles.length > 0) {
        const uploadPromises = selectedFiles.map(file =>
          uploadAttachment(file, ticket.id, null, user.id)
        );

        const uploadResults = await Promise.allSettled(uploadPromises);
        
        // Check if any uploads failed
        const failedUploads = uploadResults.filter(result => result.status === 'rejected');
        if (failedUploads.length > 0) {
          console.warn('Some file uploads failed:', failedUploads);
          // Continue anyway - ticket was created successfully
        }
      }

      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: 'medium'
      });
      setSelectedFiles([]);

      // Notify parent component
      onTicketCreated(ticket);

    } catch (error) {
      console.error('Error creating ticket:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-light">Create New Ticket</h2>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-text-muted hover:text-text-light transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title Field */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-text-light mb-2">
            Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Brief description of your issue"
            className="input-field"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Priority Field */}
        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-text-light mb-2">
            Priority
          </label>
          <select
            id="priority"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            className="input-field"
            disabled={isSubmitting}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        {/* Description Field */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-text-light mb-2">
            Description <span className="text-red-400">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Please provide detailed information about your issue, including steps to reproduce if applicable..."
            rows={6}
            className="input-field resize-vertical"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-text-light mb-2">
            Attachments
          </label>
          <FileUpload
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            selectedFiles={selectedFiles}
            disabled={isSubmitting}
            multiple={true}
          />
          <p className="text-xs text-text-muted mt-2">
            You can attach screenshots, documents, or other files to help us understand your issue better.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-banner">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border-dark/60">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 text-text-muted hover:text-text-light transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{isSubmitting ? 'Creating Ticket...' : 'Create Ticket'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTicket;
